import React, { useEffect, useRef, useState } from 'react'
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    ArrowBack,
    History,
    Upload,
    Search,
    LinkOff,
    MoreVert,
} from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import moment from 'moment'
import NoResultsAnimation from '../../../components/Shared/animations/NoResultsAnimation'
import PaginationGlobal from '../../../components/Shared/PaginationGlobal'
import SummaryBar from './components/SummaryBar'
import ImportStatementModal from './ImportStatementModal'
import ReconciliationModal from './ReconciliationModal'

const currencyBRL = (n) =>
    (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const RECONCILIATION_STATUS = [
    { key: null, label: 'Todos', color: '#64748B' },
    { key: 'false', label: 'Pendentes', color: '#F59E0B' },
    { key: 'true', label: 'Conciliados', color: '#22C55E' },
]

const cellSx = {
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E2E8F0',
}

const StatementEntries = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const typingTimerRef = useRef(null)

    const [account, setAccount] = useState(null)
    const [response, setResponse] = useState(null)
    const [summary, setSummary] = useState(null)
    const [activeStatus, setActiveStatus] = useState(null)
    const [search, setSearch] = useState(null)
    const [startDate, setStartDate] = useState(moment().startOf('month').toDate())
    const [endDate, setEndDate] = useState(moment().endOf('month').toDate())
    const [filters, setFilters] = useState({ page_size: 50, page: 1 })

    const [importModalOpen, setImportModalOpen] = useState(false)
    const [reconcileEntry, setReconcileEntry] = useState(null)
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [menuEntry, setMenuEntry] = useState(null)

    const fetchAccount = () => {
        api.get(`cashflow/managed-bank-accounts/${id}/`)
            .then((res) => setAccount(res.data))
            .catch(() => enqueueSnackbar('Erro ao carregar conta', { variant: 'error' }))
    }

    const fetchEntries = () => {
        const params = {
            account_id: id,
            condo_id: getCondoId(),
            page: filters.page,
            page_size: filters.page_size,
            date_from: moment(startDate).format('YYYY-MM-DD'),
            date_to: moment(endDate).format('YYYY-MM-DD'),
        }
        if (activeStatus !== null) params.reconciled = activeStatus
        if (search) params.search = search

        api.get('cashflow/external-statement-entries/', { params })
            .then((res) => setResponse(res.data))
            .catch(() => {
                enqueueSnackbar('Erro ao carregar lançamentos', { variant: 'error' })
                setResponse({ results: [], count: 0 })
            })
    }

    const fetchSummary = () => {
        api.get('cashflow/external-statement-entries/summary/', {
            params: {
                account_id: id,
                condo_id: getCondoId(),
                date_from: moment(startDate).format('YYYY-MM-DD'),
                date_to: moment(endDate).format('YYYY-MM-DD'),
            },
        })
            .then((res) => setSummary(res.data))
            .catch(() => {})
    }

    useEffect(() => {
        window.scrollTo(0, 0)
        fetchAccount()
    }, [id])

    useEffect(() => {
        fetchEntries()
        fetchSummary()
    }, [filters, activeStatus, search, startDate, endDate])

    const refreshData = () => {
        fetchEntries()
        fetchSummary()
        fetchAccount()
    }

    const handleUnreconcile = (entryId) => {
        api.post(`cashflow/external-statement-entries/${entryId}/unreconcile/`)
            .then(() => {
                enqueueSnackbar('Conciliação desfeita', { variant: 'success' })
                refreshData()
            })
            .catch(() => enqueueSnackbar('Erro ao desfazer conciliação', { variant: 'error' }))
        setMenuAnchor(null)
        setMenuEntry(null)
    }

    const getReconciliationBadge = (entry) => {
        if (!entry.reconciled) {
            return (
                <Chip
                    label="Pendente"
                    size="small"
                    sx={{
                        bgcolor: '#F59E0B14',
                        color: '#F59E0B',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        border: '1px solid #F59E0B30',
                    }}
                />
            )
        }
        const isAuto = entry.reconciled_type === 'automatic'
        return (
            <Chip
                label={isAuto ? 'Auto' : 'Manual'}
                size="small"
                sx={{
                    bgcolor: isAuto ? '#22C55E14' : '#3B82F614',
                    color: isAuto ? '#22C55E' : '#3B82F6',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 24,
                    border: `1px solid ${isAuto ? '#22C55E30' : '#3B82F630'}`,
                }}
            />
        )
    }

    return (
        <>
            <Helmet>
                <title>
                    Extrato{account ? ` - ${account.nickname}` : ''} - Bloco B3
                </title>
            </Helmet>

            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="xl" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={3}
                        flexWrap="wrap"
                        gap={2}
                    >
                        <Box>
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={() => navigate('/financeiro/contas-bancarias')}
                                sx={{ textTransform: 'none', color: '#64748B', mb: 0.5 }}
                            >
                                Voltar
                            </Button>
                            {account ? (
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            bgcolor: account.color || '#3B82F6',
                                        }}
                                    />
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                        {account.nickname || account.bank_name}
                                    </Typography>
                                    <Chip
                                        label={account.data_source === 'automatic' ? 'Integrada' : 'Manual'}
                                        size="small"
                                        sx={{
                                            bgcolor: account.data_source === 'automatic' ? '#22C55E14' : '#3B82F614',
                                            color: account.data_source === 'automatic' ? '#22C55E' : '#3B82F6',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            height: 24,
                                            border: `1px solid ${account.data_source === 'automatic' ? '#22C55E30' : '#3B82F630'}`,
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                                        Ag: {account.bank_branch} / Cc: {account.bank_account}
                                    </Typography>
                                </Box>
                            ) : (
                                <Skeleton width={300} height={32} />
                            )}
                        </Box>
                        <Box display="flex" gap={1.5}>
                            <Button
                                variant="outlined"
                                startIcon={<History />}
                                onClick={() =>
                                    navigate(`/financeiro/contas-bancarias/${id}/importacoes`)
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    borderColor: '#E2E8F0',
                                    color: '#64748B',
                                    '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                                }}
                            >
                                Histórico
                            </Button>
                            <Tooltip
                                title={account?.data_source === 'automatic' ? 'Conta integrada não permite importação manual' : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        startIcon={<Upload />}
                                        disabled={account?.data_source === 'automatic'}
                                        onClick={() => setImportModalOpen(true)}
                                        sx={{
                                            bgcolor: '#3B82F6',
                                            '&:hover': { bgcolor: '#2563EB' },
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            px: 3,
                                        }}
                                    >
                                        Importar Extrato
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* Summary */}
                    <SummaryBar summary={summary} />

                    {/* Filters */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <DesktopDatePicker
                                    label="Data Início"
                                    value={startDate}
                                    onChange={(date) => {
                                        setStartDate(date)
                                        setFilters((prev) => ({ ...prev, page: 1 }))
                                    }}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true },
                                    }}
                                    format="dd/MM/yyyy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <DesktopDatePicker
                                    label="Data Fim"
                                    value={endDate}
                                    onChange={(date) => {
                                        setEndDate(date)
                                        setFilters((prev) => ({ ...prev, page: 1 }))
                                    }}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true },
                                    }}
                                    format="dd/MM/yyyy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Pesquisar descrição..."
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: '#94A3B8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    onKeyUp={(e) => {
                                        clearTimeout(typingTimerRef.current)
                                        typingTimerRef.current = setTimeout(() => {
                                            setSearch(e.target.value || null)
                                            setFilters((prev) => ({ ...prev, page: 1 }))
                                        }, 800)
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" gap={1}>
                                    {RECONCILIATION_STATUS.map((opt) => (
                                        <Chip
                                            key={String(opt.key)}
                                            label={opt.label}
                                            onClick={() => {
                                                setActiveStatus(opt.key)
                                                setFilters((prev) => ({ ...prev, page: 1 }))
                                            }}
                                            sx={{
                                                bgcolor:
                                                    activeStatus === opt.key
                                                        ? `${opt.color}14`
                                                        : 'transparent',
                                                color:
                                                    activeStatus === opt.key
                                                        ? opt.color
                                                        : '#64748B',
                                                border:
                                                    activeStatus === opt.key
                                                        ? `1.5px solid ${opt.color}`
                                                        : '1.5px solid #E2E8F0',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: '#F1F5F9' },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>

                    {/* Table */}
                    <Card
                        sx={{
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        {response?.results ? (
                            <TableContainer>
                                {response.results.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                                <TableCell sx={cellSx}>Data</TableCell>
                                                <TableCell sx={cellSx}>Descrição</TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    Valor
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    Status
                                                </TableCell>
                                                <TableCell align="center" sx={{ ...cellSx, width: 60 }}>
                                                    Ações
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {response.results.map((entry) => (
                                                <TableRow
                                                    key={entry.id}
                                                    sx={{
                                                        cursor: !entry.reconciled ? 'pointer' : 'default',
                                                        transition: 'background-color 0.15s ease',
                                                        '&:hover': { bgcolor: '#F1F5F9' },
                                                        '& td': { borderBottom: '1px solid #F1F5F9' },
                                                    }}
                                                    onClick={() => {
                                                        if (!entry.reconciled) setReconcileEntry(entry)
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ color: '#64748B', fontSize: '0.85rem' }}
                                                        >
                                                            {moment(entry.entry_date).format('DD/MM/YYYY')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#1E293B',
                                                                fontWeight: 500,
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {entry.description}
                                                        </Typography>
                                                        {entry.reconciled && entry.metadata?.description && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: '#94A3B8', display: 'block' }}
                                                            >
                                                                ↳ {entry.metadata.description}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color:
                                                                    entry.entry_value >= 0
                                                                        ? '#22C55E'
                                                                        : '#EF4444',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        >
                                                            {entry.entry_value >= 0 ? '+' : ''}
                                                            {currencyBRL(entry.entry_value)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getReconciliationBadge(entry)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {entry.reconciled ? (
                                                            <Tooltip title="Desfazer conciliação">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setMenuAnchor(e.currentTarget)
                                                                        setMenuEntry(entry)
                                                                    }}
                                                                >
                                                                    <MoreVert
                                                                        sx={{ fontSize: 18, color: '#94A3B8' }}
                                                                    />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setReconcileEntry(entry)
                                                                }}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 600,
                                                                    color: '#3B82F6',
                                                                    minWidth: 'auto',
                                                                }}
                                                            >
                                                                Conciliar
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Box sx={{ py: 6 }}>
                                        <NoResultsAnimation />
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#94A3B8', textAlign: 'center', mt: 1 }}
                                        >
                                            Nenhum lançamento encontrado. Importe seu primeiro extrato.
                                        </Typography>
                                    </Box>
                                )}
                            </TableContainer>
                        ) : (
                            <Skeleton variant="rounded" height={400} animation="pulse" sx={{ m: 2 }} />
                        )}
                    </Card>

                    {response?.count > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PaginationGlobal
                                state={response}
                                filters={filters}
                                setFilters={setFilters}
                            />
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Context menu for reconciled entries */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null)
                    setMenuEntry(null)
                }}
            >
                <MenuItem
                    onClick={() => handleUnreconcile(menuEntry?.id)}
                    sx={{ color: '#EF4444', fontSize: '0.85rem' }}
                >
                    <LinkOff sx={{ fontSize: 18, mr: 1 }} />
                    Desfazer Conciliação
                </MenuItem>
            </Menu>

            <ImportStatementModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                accountId={parseInt(id)}
                onSuccess={refreshData}
            />

            {reconcileEntry && (
                <ReconciliationModal
                    entry={reconcileEntry}
                    onClose={() => setReconcileEntry(null)}
                    onReconcile={refreshData}
                />
            )}
        </>
    )
}

export default StatementEntries
